import {
  MapPinIcon,
  ForkKnifeIcon,
  CarIcon,
  BuildingsIcon,
} from "@phosphor-icons/react";
import clsx from "clsx";
import { useMemo, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import { AirplaneTakeoffIcon } from "@/components/Icons/rtlReadyIcons";
import type { TripDetails } from "@/features/trips/types/types";

const iconMap: Record<string, React.ElementType> = {
  MapPin: MapPinIcon,
  ForkKnife: ForkKnifeIcon,
  Car: CarIcon,
  Buildings: BuildingsIcon,
  AirplaneTakeoff: AirplaneTakeoffIcon,
};

type Props = {
  title: ReactNode;
  features: TripDetails["features"];
  type: TripDetails["features"][number]["type"];
};

function FeaturesCard({ title, features, type }: Props) {
  const filteredFeatures = useMemo(
    () => features.filter((feature) => feature.type === type),
    [features, type],
  );

  if (filteredFeatures.length === 0) {
    return null;
  }

  return (
    <CollapsibleCard
      title={title}
      collapsible={type === "EXCLUDED"}
      expandedByDefault={type === "INCLUDED"}
    >
      <ul className="space-y-2">
        {filteredFeatures.map((feature) => {
          const IconComponent = iconMap[feature.icon];

          return (
            <li key={feature.id} className="flex items-center gap-2">
              <span
                className={clsx(
                  "flex size-8 shrink-0 items-center justify-center rounded-xl",
                  {
                    "bg-surface-accent-softer text-surface-accent-strong":
                      type === "INCLUDED",
                    "bg-surface-background text-text-tertiary":
                      type === "EXCLUDED",
                  },
                )}
                aria-hidden
              >
                <IconComponent className="size-4" />
              </span>
              <span className="text-sm font-medium text-text-primary">
                {feature.name}
              </span>
            </li>
          );
        })}
      </ul>
    </CollapsibleCard>
  );
}

export function InclusionsCard({ trip }: { trip: TripDetails }) {
  const t = useTranslations();

  return (
    <FeaturesCard
      title={t("trips.inclusions.title")}
      features={trip.features}
      type="INCLUDED"
    />
  );
}

export function ExclusionsCard({ trip }: { trip: TripDetails }) {
  const t = useTranslations();

  return (
    <FeaturesCard
      title={t("trips.exclusions.title")}
      features={trip.features}
      type="EXCLUDED"
    />
  );
}
